import { useState, useCallback } from 'react';
import { ScrollView, View, TouchableOpacity, Text, StyleSheet, ActivityIndicator, TextInput, RefreshControl } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '@/hooks/useAuth';
import { useStudents } from '@/hooks/useStudents';
import { useStudentEngagement, EngagementStatus } from '@/hooks/useStudentEngagement';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { StudentsDesktop } from '@/components/StudentsDesktop';
import { Student } from '@/lib/types';
import { isOnline, relativeCoarse } from '@/lib/relative-time';

const STATUS_CARD_BORDER = {
  green:  { borderLeftWidth: 4 as const, borderLeftColor: '#2E7D32' },
  yellow: { borderLeftWidth: 4 as const, borderLeftColor: '#F9A825' },
  red:    { borderLeftWidth: 4 as const, borderLeftColor: '#C8102E' },
} satisfies Record<EngagementStatus, object>;

const STATUS_PRIORITY: Record<EngagementStatus, number> = { red: 0, yellow: 1, green: 2 };

function StudentCard({ student, onPress, engagementStatus }: {
  student: Student;
  onPress: () => void;
  engagementStatus?: EngagementStatus;
}) {
  const borderStyle = engagementStatus ? STATUS_CARD_BORDER[engagementStatus] : {};
  // Presença: bolinha verde sobreposta ao avatar se online (<5min);
  // senão escala "grossa" (hoje/ontem/esta semana/...) — granularidade
  // adequada pra PT acompanhar sem virar vigilância minuciosa.
  const online = isOnline(student.last_seen_at);
  const presenceText = online
    ? 'online agora'
    : student.last_seen_at
      ? `Último login: ${relativeCoarse(student.last_seen_at)}`
      : null;
  return (
    <TouchableOpacity
      style={[styles.studentCard, borderStyle]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <View style={styles.avatarWrap}>
        <View style={styles.studentAvatar}>
          <Text style={styles.studentInitial}>
            {student.full_name?.charAt(0)?.toUpperCase() ?? '?'}
          </Text>
        </View>
        {online && <View style={styles.onlineDot} />}
      </View>
      <View style={{ flex: 1 }}>
        <Text style={styles.studentName}>{student.full_name ?? student.email}</Text>
        {student.goal ? <Text style={styles.studentGoal}>{student.goal}</Text> : null}
        {presenceText && (
          <Text
            style={[styles.presence, online && styles.presenceOnline]}
            numberOfLines={1}
          >
            {presenceText}
          </Text>
        )}
      </View>
      <Ionicons name="chevron-forward" size={16} color="#ccc" />
    </TouchableOpacity>
  );
}

export default function StudentsScreen() {
  const { profile } = useAuth();
  const isDesktop = useIsDesktop();
  const { students, loading, refetch } = useStudents(profile?.id ?? '');
  const router = useRouter();
  const [inactivesOpen, setInactivesOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  if (isDesktop) return <StudentsDesktop />;

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  }, [refetch]);

  const searchLower = search.toLowerCase();
  const matchesSearch = (s: Student) =>
    !search ||
    (s.full_name ?? '').toLowerCase().includes(searchLower) ||
    s.email.toLowerCase().includes(searchLower);

  const activeStudents = students.filter(s => s.status === 'active');
  const waitingStudents = students.filter(s => s.status === 'waiting')
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
  const inactiveStudents = students.filter(s => s.status === 'inactive');

  const { statuses: engagement, isLoading: engagementLoading } = useStudentEngagement(
    activeStudents.map(s => s.id),
  );

  const greenCount  = activeStudents.filter(s => (engagement[s.id] ?? 'red') === 'green').length;
  const yellowCount = activeStudents.filter(s => (engagement[s.id] ?? 'red') === 'yellow').length;
  const redCount    = activeStudents.filter(s => (engagement[s.id] ?? 'red') === 'red').length;

  const sortedActive = [...activeStudents]
    .filter(matchesSearch)
    .sort((a, b) => {
      const pa = STATUS_PRIORITY[engagement[a.id] ?? 'red'];
      const pb = STATUS_PRIORITY[engagement[b.id] ?? 'red'];
      if (pa !== pb) return pa - pb;
      return (a.full_name ?? a.email ?? '').localeCompare(b.full_name ?? b.email ?? '');
    });

  const filteredWaiting = waitingStudents.filter(matchesSearch);
  const filteredInactive = inactiveStudents.filter(matchesSearch);

  if (loading || engagementLoading) return <ActivityIndicator style={{ flex: 1 }} color="#C8102E" />;

  return (
    <View style={styles.container}>
      <View style={styles.searchContainer}>
        <Ionicons name="search-outline" size={16} color="#aaa" style={{ marginRight: 6 }} />
        <TextInput
          style={styles.searchInput}
          placeholder="Buscar aluno..."
          placeholderTextColor="#bbb"
          value={search}
          onChangeText={setSearch}
          clearButtonMode="while-editing"
          autoCorrect={false}
        />
        {search.length > 0 && (
          <TouchableOpacity onPress={() => setSearch('')}>
            <Ionicons name="close-circle" size={16} color="#bbb" />
          </TouchableOpacity>
        )}
      </View>
      <ScrollView
        contentContainerStyle={{ padding: 16, paddingBottom: 100 }}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#C8102E']} tintColor="#C8102E" />}
      >

        {/* Engagement summary — active students only */}
        {activeStudents.length > 0 && (
          <View style={styles.engagementRow}>
            <View style={styles.engagementItem}>
              <Text style={styles.engagementCount}>{greenCount}</Text>
              <Text style={styles.engagementLabel}>🟢 essa semana</Text>
            </View>
            <View style={styles.engagementDivider} />
            <View style={styles.engagementItem}>
              <Text style={styles.engagementCount}>{yellowCount}</Text>
              <Text style={styles.engagementLabel}>🟡 sem. passada</Text>
            </View>
            <View style={styles.engagementDivider} />
            <View style={styles.engagementItem}>
              <Text style={styles.engagementCount}>{redCount}</Text>
              <Text style={styles.engagementLabel}>🔴 sem treinar</Text>
            </View>
          </View>
        )}

        {sortedActive.length === 0 && filteredWaiting.length === 0 && filteredInactive.length === 0 ? (
          <Text style={styles.empty}>
            {search ? 'Nenhum aluno encontrado.' : 'Nenhum aluno cadastrado ainda.'}
          </Text>
        ) : (
          <>
            {/* Aguardando ativação */}
            {filteredWaiting.length > 0 && (
              <>
                <View style={styles.waitingHeader}>
                  <Text style={styles.waitingHeaderText}>
                    ⏳ Aguardando ativação ({filteredWaiting.length})
                  </Text>
                </View>
                {filteredWaiting.map(student => (
                  <TouchableOpacity
                    key={student.id}
                    style={styles.waitingCard}
                    onPress={() => router.push(`/(personal)/students/${student.id}` as any)}
                    activeOpacity={0.7}
                  >
                    <View style={styles.studentAvatar}>
                      <Text style={styles.studentInitial}>
                        {student.full_name?.charAt(0)?.toUpperCase() ?? '?'}
                      </Text>
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.studentName}>{student.full_name ?? student.email}</Text>
                      <Text style={styles.waitingSubtitle}>Toque para ver ficha e ativar</Text>
                    </View>
                    <Ionicons name="chevron-forward" size={16} color="#F9A825" />
                  </TouchableOpacity>
                ))}
                <View style={styles.waitingSeparator} />
              </>
            )}

            {sortedActive.length > 0 && (
              <>
                <Text style={styles.sectionHeader}>Ativos ({sortedActive.length})</Text>
                {sortedActive.map(student => (
                  <StudentCard
                    key={student.id}
                    student={student}
                    engagementStatus={engagement[student.id] ?? 'red'}
                    onPress={() => router.push(`/(personal)/students/${student.id}` as any)}
                  />
                ))}
              </>
            )}

            {filteredInactive.length > 0 && (
              <>
                <TouchableOpacity
                  style={styles.inactiveTrayHeader}
                  onPress={() => setInactivesOpen(v => !v)}
                  activeOpacity={0.7}
                >
                  <Text style={styles.inactiveTrayTitle}>
                    Inativos ({filteredInactive.length})
                  </Text>
                  <Ionicons
                    name={inactivesOpen ? 'chevron-up' : 'chevron-down'}
                    size={16}
                    color="#aaa"
                  />
                </TouchableOpacity>
                {inactivesOpen && filteredInactive.map(student => (
                  <StudentCard
                    key={student.id}
                    student={student}
                    onPress={() => router.push(`/(personal)/students/${student.id}` as any)}
                  />
                ))}
              </>
            )}
          </>
        )}
      </ScrollView>

      <TouchableOpacity style={styles.fab} onPress={() => router.push('/(personal)/students/new' as any)}>
        <Text style={styles.fabText}>+ Aluno</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F5F5F5' },
  searchContainer: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#fff', marginHorizontal: 16, marginTop: 12, marginBottom: 4,
    borderRadius: 10, paddingHorizontal: 12, paddingVertical: 8,
    borderWidth: 1.5, borderColor: '#eee',
  },
  searchInput: { flex: 1, fontSize: 14, color: '#1A1A1A', padding: 0 },
  empty: { textAlign: 'center', color: '#aaa', marginTop: 40, fontSize: 15 },
  sectionHeader: {
    fontSize: 11, fontWeight: '700', color: '#888',
    textTransform: 'uppercase', letterSpacing: 0.8,
    marginBottom: 8,
  },
  studentCard: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    backgroundColor: '#fff', borderRadius: 12, padding: 14, marginBottom: 8,
    borderWidth: 1.5, borderColor: '#eee',
  },
  studentAvatar: {
    width: 40, height: 40, borderRadius: 20,
    backgroundColor: '#C8102E22', alignItems: 'center', justifyContent: 'center',
  },
  // Wrapper relativo pro avatar + bolinha de presença sobreposta no canto inferior direito
  avatarWrap: { position: 'relative' },
  onlineDot: {
    position: 'absolute', bottom: 0, right: 0,
    width: 12, height: 12, borderRadius: 6,
    backgroundColor: '#2E7D32', borderWidth: 2, borderColor: '#fff',
  },
  studentInitial: { fontSize: 17, fontWeight: '700', color: '#C8102E' },
  studentName: { fontSize: 15, fontWeight: '600', color: '#1A1A1A' },
  studentGoal: { fontSize: 12, color: '#888', marginTop: 2 },
  presence: { fontSize: 11, color: '#aaa', marginTop: 2 },
  presenceOnline: { color: '#2E7D32', fontWeight: '600' },
  engagementRow: {
    flexDirection: 'row', backgroundColor: '#fff', borderRadius: 12,
    padding: 14, marginBottom: 16, justifyContent: 'space-around',
    borderWidth: 1.5, borderColor: '#eee',
  },
  engagementItem: { alignItems: 'center' },
  engagementCount: { fontSize: 20, fontWeight: 'bold', color: '#1A1A1A' },
  engagementLabel: { fontSize: 11, color: '#888', marginTop: 2 },
  engagementDivider: { width: 1, backgroundColor: '#eee' },
  waitingHeader: {
    backgroundColor: '#FFF8E1', borderRadius: 10, paddingHorizontal: 14, paddingVertical: 10,
    marginBottom: 8, borderLeftWidth: 4, borderLeftColor: '#F9A825',
  },
  waitingHeaderText: { fontSize: 13, fontWeight: '700', color: '#E65100' },
  waitingCard: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    backgroundColor: '#FFFDE7', borderRadius: 12, padding: 14, marginBottom: 8,
    borderWidth: 1.5, borderColor: '#FFE082',
  },
  waitingSubtitle: { fontSize: 12, color: '#F9A825', marginTop: 2, fontWeight: '500' },
  waitingSeparator: { height: 1, backgroundColor: '#eee', marginBottom: 16 },
  inactiveTrayHeader: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingVertical: 12, paddingHorizontal: 4, marginTop: 20,
  },
  inactiveTrayTitle: { fontSize: 11, fontWeight: '700', color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.8 },
  fab: {
    position: 'absolute', bottom: 24, right: 24,
    backgroundColor: '#C8102E', paddingHorizontal: 20, paddingVertical: 14,
    borderRadius: 28, elevation: 4,
  },
  fabText: { color: '#fff', fontWeight: 'bold', fontSize: 15 },
});
